import { EmmaImportantNoticeBanner } from '@axa-asia/emma-stylesheet';
import { throttle } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCmsProfile,
  getLanguage,
} from '../../../../selectors/userSelectors';
import { t } from '../../../../locales';
import prismicApiClient from '../../../../utils/prismicApi';
import { closeImportNotice } from '../../actions';
import {
  ClosedImportantNoticeState,
  ImportantNoticeAudience,
  NoticeItem,
} from '../../configs/type';
import { getClosedImportantNoticeLocalState } from '../../selectors/importantNoticeSelector';
import { gaBannerClick } from '../../../offers/utils/prismicBannerUtils';
import { openPrismicLink } from '../../../../actions/prismicActions';
import { Lang } from '@axa-asia/emma-stylesheet/lib/common/types';

type ImportantNoticeProps = {
  audience: ImportantNoticeAudience;
  className?: string;
};

export const ImportantNotice: FC<ImportantNoticeProps> = ({
  audience,
  className,
}) => {
  const CWP_PROFILES = ['CWP'];

  const dispatch = useDispatch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const language = useSelector(getLanguage) as Lang;
  const closedImportantNoticeLocalState: ClosedImportantNoticeState[] =
    useSelector(getClosedImportantNoticeLocalState);

  const cmsProfiles = useSelector(getCmsProfile) || CWP_PROFILES;

  const noticeItems = prismicApiClient
    .getImportantNotices({
      platform: 'WEB',
      lang: language || 'en',
      tags: cmsProfiles,
      messageAudience: audience,
      limit: 6,
    })
    .useHook({ cacheTime: 0 });

  const closedRecord = useMemo<Set<string>>(() => {
    if (
      !closedImportantNoticeLocalState ||
      !Array.isArray(closedImportantNoticeLocalState)
    ) {
      return new Set();
    }

    return new Set(
      closedImportantNoticeLocalState.map((notice) => notice.noticeId),
    );
  }, [closedImportantNoticeLocalState]);

  const noticeItemsData: NoticeItem[] = useMemo<NoticeItem[]>(() => {
    if (!noticeItems?.data) {
      return [];
    }

    return noticeItems.data
      ?.map((notice) => {
        const { data, uid, id } = notice;
        const existCtaBtn =
          data?.landing_url_web?.link && data?.landing_url_web?.link != '#';
        const buttonText = existCtaBtn
          ? data?.button_text ?? t('IMPORTANT_NOTICE_CTA_BTN')
          : undefined;

        return {
          id,
          uid,
          buttonText,
          existCtaBtn,
          icon: data?.notice_icon,
          title: data?.header_title,
          closeMode: data?.close_mode,
          content: data?.content,
          showCloseIcon: data?.should_show_close_icon,
          link: data?.landing_url_web,
          linkQueue: data?.landing_url_queue,
          linkType: data?.landing_url_type,
          gaCampaignName: data?.ga_campaign_name,
          gaBannerCategory: data?.ga_banner_category,
          gaCustomBannerCategory: data?.ga_custom_banner_category,
        };
      })
      ?.filter((notice) => !closedRecord.has(notice.uid));
  }, [noticeItems.data, closedRecord, audience]);

  useEffect(() => {
    const noticeItem = noticeItemsData?.[currentIndex];
    if (!noticeItem) {
      return;
    }

    gaBannerClick(
      cmsProfiles,
      `IN - ${noticeItem.title}`,
      'display',
      noticeItem.gaCampaignName,
      noticeItem.gaBannerCategory,
      noticeItem.gaCustomBannerCategory,
    );
  }, [noticeItemsData[currentIndex]?.uid]);

  const throttledOnClose = useMemo(
    () =>
      throttle((index: number) => {
        const noticeItem = noticeItemsData?.[index];
        if (!noticeItem) {
          return;
        }

        dispatch(closeImportNotice(noticeItem.uid, noticeItem.closeMode));

        gaBannerClick(
          cmsProfiles,
          `IN - ${noticeItem.title}`,
          'close',
          noticeItem.gaCampaignName,
          noticeItem.gaBannerCategory,
          noticeItem.gaCustomBannerCategory,
        );
      }, 1000),
    [dispatch, noticeItemsData, cmsProfiles],
  );

  const throttledOnPress = useMemo(
    () =>
      throttle((index: number) => {
        const noticeItem = noticeItemsData?.[index];
        if (!noticeItem || !noticeItem.existCtaBtn) {
          return;
        }

        const {
          title,
          link,
          linkQueue,
          linkType,
          gaCampaignName,
          gaBannerCategory,
          gaCustomBannerCategory,
        } = noticeItem;

        gaBannerClick(
          cmsProfiles,
          `IN - ${title}`,
          'click',
          gaCampaignName,
          gaBannerCategory,
          gaCustomBannerCategory,
        );

        openPrismicLink(link, linkQueue, linkType, language, null);
      }, 1000),
    [cmsProfiles, noticeItemsData],
  );

  if (noticeItemsData.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <EmmaImportantNoticeBanner
        notices={noticeItemsData}
        currentIndex={currentIndex}
        onIndexChange={setCurrentIndex}
        onClose={throttledOnClose}
        onPress={throttledOnPress}
      />
    </div>
  );
};
